<script setup>
import { reactive, onMounted, nextTick } from "vue";
import bus from "@/bus";
import { logEvent } from "@/utils/tracking";
import trackedEvents from "@/utils/trackedEvents";
import SuccessIcon from "@/assets/icons/toast/success-icon.svg";
import ErrorIcon from "@/assets/icons/toast/error-icon.svg";
import InfoIcon from "@/assets/icons/toast/info-icon.svg";
import CreditsIcon from "@/assets/icons/credits.svg";
import { onUnmounted } from "vue";

const data = reactive({
  show: false,
  title: undefined,
  type: "info",
  message: "Default message",
  duration: 4000,
  timer: undefined,
  credits: undefined,
  action: () => {
    hideNotification();
  },
});

const hideNotification = () => {
  data.show = false;
  data.title = undefined;
  data.type = "info";
  data.message = "Default message";
  data.action = () => {
    hideNotification();
  };
  clearTimeout(data.timer);
  data.credits = undefined;
};

const show = (properties) => {
  // if we're already showing a notification, wait for it to finish. This is actually queue of notifications
  if (data.show) {
    setTimeout(() => nextTick(() => show(properties)), 250);
    return;
  }
  data.show = true;
  data.type = properties.type || "info";
  data.title = properties.title;
  data.message = properties.message;
  data.duration = properties.duration || 4000;
  data.credits = properties.credits || undefined;
  if (properties.action) {
    data.action = () => {
      hideNotification();
      properties.action();
    };
  }
  data.timer = setTimeout(() => {
    hideNotification();
  }, data.duration);

  if (properties.type === "error") {
    const { title, message } = properties;
    const errorMessage = title && message ? `${title}: ${message}` : (title || message || "");
    logEvent(trackedEvents.ERROR_ENCOUNTERED, { message: errorMessage });
  }
};

onMounted(() => {
  bus.global.on("notification.top:show", show);
  bus.global.on("notification.top:hide", () => {
    hideNotification();
  });
});
onUnmounted(() => {
  bus.global.off("notification.top:show");
  bus.global.off("notification.top:hide");
});
</script>

<template>
  <transition name="fade">
    <div v-if="data.show" class="toast-wrapper" @click="data.action">
      <div class="toast">
        <div class="icon-wrapper">
          <SuccessIcon v-if="data.type === 'success'" class="icon" />
          <ErrorIcon v-if="data.type === 'error'" class="icon" />
          <InfoIcon v-if="data.type === 'info'" class="icon" />
        </div>
        <div class="text-block">
          <div v-if="data.title" class="title">{{ data.title }}</div>
          <div class="message">
            {{ data.message
            }}<CreditsIcon v-if="data.credits" class="credits-icon" />
          </div>
        </div>
        <div v-if="data.credits" class="credits">
          <b class="title">{{ data.credits }}</b>
          <CreditsIcon class="credits-icon" />
        </div>
        <div class="ellipse" :class="data.type"></div>
      </div>
    </div>
  </transition>
</template>

<style lang="scss" scoped>
.toast-wrapper {
  position: absolute;
  top: 10px;
  right: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4000;
}
.toast {
  background: #ffffff;
  border-radius: 8px;
  padding: 12px 16px 12px 16px;
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
  justify-content: flex-start;
  width: 80%;
  height: 100%;
  box-shadow: var(
    --16-dp-box-shadow,
    0px 8px 10px 0px rgba(0, 0, 0, 0.2),
    0px 6px 30px 0px rgba(0, 0, 0, 0.12),
    0px 16px 24px 0px rgba(0, 0, 0, 0.14)
  );
  overflow: hidden;
  position: relative;
}
.icon-wrapper {
  background: #303746;
  border-radius: 43px;
  padding: 4px;
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: flex-start;
  justify-content: flex-start;
  flex-shrink: 0;
  position: relative;
}
.icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  position: relative;
  overflow: visible;
}
.text-block {
  display: flex;
  flex-direction: column;
  gap: 0px;
  align-items: flex-start;
  justify-content: flex-start;
  flex: 1;
  position: relative;
}
.title {
  color: #000000;
  text-align: left;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: -0.41px;
  font-weight: 500;
  position: relative;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: flex-start;
}
.message {
  color: #7d7d7d;
  text-align: left;
  font-size: 12px;
  line-height: 18px;
  letter-spacing: -0.08px;
  font-weight: 400;
  position: relative;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.ellipse {
  border-radius: 50%;
  flex-shrink: 0;
  width: 212px;
  height: 212px;
  position: absolute;
  left: -74px;
  top: -65px;
  &.success {
    background: radial-gradient(
      closest-side,
      rgba(0, 237, 81, 0.12) 0%,
      rgba(0, 237, 123, 0) 100%
    );
  }
  &.error {
    background: radial-gradient(
      closest-side,
      rgba(240, 66, 72, 0.12) 0%,
      rgba(240, 66, 72, 0) 100%
    );
  }
  &.info {
    background: radial-gradient(
      closest-side,
      rgba(255, 212, 38, 0.12) 0%,
      rgba(255, 212, 38, 0) 100%
    );
  }
}
.credits-icon {
  width: 12px;
  height: 12px;
  padding-left: 2px;
}
.credits {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  .credits-icon {
    width: 16px;
    height: 16px;
  }
}
</style>
